// app/javascript/controllers/ui/manifesto/webgl_blobs_controller.js

import { Controller } from "@hotwired/stimulus"

/**
 * WebGL Blobs Controller
 *
 * Рендерит два gradient blob'а с noise через WebGL2 fragment shader.
 * Лёгкий: один фулл-скрин quad, без Three.js, без зависимостей.
 *
 * @usedby app/views/welcome/_hero.html.erb (manifesto redesign)
 *
 * Usage:
 *   <canvas data-controller="ui--manifesto--webgl-blobs"
 *           data-ui--manifesto--webgl-blobs-color1-value="#6C4DFF"
 *           data-ui--manifesto--webgl-blobs-color2-value="#CDFF5E"
 *           class="manifesto__blobs"></canvas>
 *
 * Отключается автоматически:
 *   - на prefers-reduced-motion
 *   - когда canvas вне viewport (экономия CPU)
 */
export default class extends Controller {
  static values = {
    color1: { type: String, default: "#6C4DFF" },
    color2: { type: String, default: "#CDFF5E" },
    speed:  { type: Number, default: 0.15 }
  }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.canvas = this.element
    this.gl = this.canvas.getContext("webgl2", { antialias: false, premultipliedAlpha: false })
    if (!this.gl) return

    this.running = true
    this.startTime = performance.now()
    this.#setupShader()
    this.#resize()
    this.#observeVisibility()

    this.resizeHandler = () => this.#resize()
    window.addEventListener("resize", this.resizeHandler, { passive: true })

    this.#loop()
  }

  disconnect() {
    this.running = false
    if (this.raf) cancelAnimationFrame(this.raf)
    if (this.resizeHandler) window.removeEventListener("resize", this.resizeHandler)
    if (this.observer) this.observer.disconnect()
  }

  #observeVisibility() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(e => { this.visible = e.isIntersecting })
    }, { threshold: 0 })
    this.observer.observe(this.canvas)
    this.visible = true
  }

  #setupShader() {
    const gl = this.gl

    const vsSrc = `#version 300 es
      in vec2 a;
      out vec2 uv;
      void main() {
        uv = a * 0.5 + 0.5;
        gl_Position = vec4(a, 0.0, 1.0);
      }`

    const fsSrc = `#version 300 es
      precision highp float;
      in vec2 uv;
      out vec4 fragColor;
      uniform float uTime;
      uniform vec2  uRes;
      uniform vec3  uColor1;
      uniform vec3  uColor2;

      // Hash-based noise
      float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float noise(vec2 p) {
        vec2 i = floor(p), f = fract(p);
        vec2 u = f*f*(3.0-2.0*f);
        return mix(mix(hash(i), hash(i+vec2(1,0)), u.x),
                   mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), u.x), u.y);
      }
      float fbm(vec2 p) {
        float v = 0.0, a = 0.5;
        for (int i = 0; i < 4; i++) {
          v += a * noise(p);
          p *= 2.0;
          a *= 0.5;
        }
        return v;
      }

      // Soft circle with distance falloff
      float blob(vec2 uv, vec2 center, float r, float softness) {
        float d = length(uv - center);
        return smoothstep(r + softness, r - softness, d);
      }

      void main() {
        vec2 aspect = vec2(uRes.x / uRes.y, 1.0);
        vec2 p = (uv - 0.5) * aspect;
        float t = uTime * 0.18;

        // Blob 1 — moves in a lissajous curve
        vec2 c1 = vec2(
          0.35 + 0.20 * sin(t * 1.1),
          0.15 + 0.20 * cos(t * 0.9)
        );
        float b1 = blob(p, c1, 0.55, 0.55);

        // Blob 2 — slower, opposite motion
        vec2 c2 = vec2(
          -0.40 + 0.25 * cos(t * 0.7),
          -0.20 + 0.22 * sin(t * 1.3)
        );
        float b2 = blob(p, c2, 0.60, 0.55);

        // Noise perturbation
        float n = fbm(p * 2.0 + vec2(t * 0.3, -t * 0.2));
        b1 *= 0.6 + 0.4 * n;
        b2 *= 0.6 + 0.4 * (1.0 - n);

        vec3 col = uColor1 * b1 + uColor2 * b2;

        // Vignette fadeout to edges
        float vig = smoothstep(1.4, 0.2, length(p));
        col *= vig;

        fragColor = vec4(col, 1.0);
      }`

    const vs = this.#compile(gl.VERTEX_SHADER, vsSrc)
    const fs = this.#compile(gl.FRAGMENT_SHADER, fsSrc)
    const prog = gl.createProgram()
    gl.attachShader(prog, vs)
    gl.attachShader(prog, fs)
    gl.linkProgram(prog)
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn("[webgl-blobs] link fail", gl.getProgramInfoLog(prog))
      return
    }
    this.prog = prog

    // Fullscreen quad
    const buf = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buf)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW)
    const loc = gl.getAttribLocation(prog, "a")
    gl.enableVertexAttribArray(loc)
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0)

    this.uTime = gl.getUniformLocation(prog, "uTime")
    this.uRes  = gl.getUniformLocation(prog, "uRes")
    this.uC1   = gl.getUniformLocation(prog, "uColor1")
    this.uC2   = gl.getUniformLocation(prog, "uColor2")

    gl.useProgram(prog)
    const c1 = this.#hexToRgb(this.color1Value)
    const c2 = this.#hexToRgb(this.color2Value)
    gl.uniform3f(this.uC1, c1.r, c1.g, c1.b)
    gl.uniform3f(this.uC2, c2.r, c2.g, c2.b)
  }

  #compile(type, src) {
    const gl = this.gl
    const s = gl.createShader(type)
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.warn("[webgl-blobs] compile fail", gl.getShaderInfoLog(s))
    }
    return s
  }

  #resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const { clientWidth: w, clientHeight: h } = this.canvas
    this.canvas.width  = Math.max(1, Math.floor(w * dpr))
    this.canvas.height = Math.max(1, Math.floor(h * dpr))
    if (this.gl) this.gl.viewport(0, 0, this.canvas.width, this.canvas.height)
  }

  #loop() {
    if (!this.running) return
    this.raf = requestAnimationFrame(() => this.#loop())
    if (!this.visible || !this.prog) return

    const gl = this.gl
    const t = (performance.now() - this.startTime) / 1000 * this.speedValue
    gl.uniform1f(this.uTime, t)
    gl.uniform2f(this.uRes, this.canvas.width, this.canvas.height)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
  }

  #hexToRgb(hex) {
    const clean = hex.replace("#", "")
    const n = parseInt(clean, 16)
    return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 }
  }
}
